import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Assignment, ExpandMore } from "@mui/icons-material";
import InfoIcon from "@mui/icons-material/Info";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import CustomTable from "../../../components/CustomTable";
import {
  createPersonnelAssignmentHistory,
  deletePersonnelAssignmentHistory,
  getPersonnelAssignmentHistory,
  updatePersonnelAssignmentHistory,
} from "../../../reducers/PersonnelAssignmentHistoryReducer";
import AssignmentHistoryDialog from "./AssignmentHistoryDialog";
import { formatDateToMonthDateYear } from "../../../utils/functions";
import { updateEmployee } from "../../../reducers/EmployeeReducer";

const tableHeaders = [
  "Effective Date",
  "Department/Division",
  "Department/Division Date",
  "Title Position",
  "Rank",
  "Section",
  "Section Assigned Date",
  "Actions",
];

const AssignmentHistoryTable = ({
  personnelId,
  departmentOptions,
  titlePositionOptions,
  rankOptions,
  sectionOptions,
  personnelAssignmentHistory,
  theme,
  allExpanded = false,
  onAssignmentUpdate,
  latestAssignment,
  canAdd,
  canEdit,
  canDelete,
  readOnly,
}) => {
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);
  const [openAssignmentHistoryDialog, setOpenAssignmentHistoryDialog] =
    useState(false);
  const [editingAssignmentHistory, setEditingAssignmentHistory] =
    useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [assignmentHistoryToDelete, setAssignmentHistoryToDelete] =
    useState(null);
  const [expanded, setExpanded] = useState(allExpanded);
  const [openDialog, setOpenDialog] = useState(false);

  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  useEffect(() => {
    if (personnelId) {
      dispatch(getPersonnelAssignmentHistory(personnelId));
    }
  }, [personnelId, dispatch]);

  const handleOpenAddAssignmentHistory = (e) => {
    e.stopPropagation();
    setEditingAssignmentHistory(null);
    setOpenAssignmentHistoryDialog(true);
  };

  const handleOpenAssignmentHistory = (e, ah) => {
    e.stopPropagation();
    setEditingAssignmentHistory(ah);
    setOpenAssignmentHistoryDialog(true);
  };

  const handleSaveAssignmentHistory = async (data) => {
    const id = personnelId;
    const payload = { ...data, personnelId: id };

    if (editingAssignmentHistory) {
      await dispatch(
        updatePersonnelAssignmentHistory(editingAssignmentHistory.id, payload)
      );
      await dispatch(getPersonnelAssignmentHistory(id));
    } else {
      await dispatch(createPersonnelAssignmentHistory(payload));
      await dispatch(getPersonnelAssignmentHistory(id));

      const updatedEmployeeData = {
        trooperDate: data.effectiveDate,
        department: data.department,
        departmentDate: data.departmentDate,
        titlePosition: data.titlePosition,
        rank: data.rank,
        section: data.section,
        sectionDate: data.sectionDate,
      };

      await dispatch(updateEmployee(id, updatedEmployeeData));
      onAssignmentUpdate(updatedEmployeeData);
    }
  };

  const handleOpenDeleteDialog = (e, ecId) => {
    e.stopPropagation();
    setAssignmentHistoryToDelete(ecId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (assignmentHistoryToDelete) {
      dispatch(
        deletePersonnelAssignmentHistory(
          assignmentHistoryToDelete,
          userData?.user?.ptsUserID,
          personnelId
        )
      ).then(() => dispatch(getPersonnelAssignmentHistory(personnelId)));
    }
    setOpenDeleteDialog(false);
    setAssignmentHistoryToDelete(null);
  };

  const openDataDialog = () => {
    setOpenDialog(true);
  };

  const renderAssignmentData = () => {
    if (!latestAssignment) return null;

    const {
      trooperDate,
      department,
      departmentDate,
      section,
      sectionDate,
      titlePosition,
      rank,
    } = latestAssignment;

    const items = [];

    if (trooperDate) {
      items.push(
        <Box key="date">
          {isSmall ? "" : "Current Assignment- "}Effective Date: {trooperDate}
        </Box>
      );
    }

    if (department && departmentDate) {
      items.push(
        <Box key="department">
          Department/Division: {department} ({departmentDate})
        </Box>
      );
    }

    if (section && sectionDate) {
      items.push(
        <Box key="section">
          Section: {section} ({sectionDate})
        </Box>
      );
    }

    if (titlePosition) {
      items.push(<Box key="title">Title/Position: {titlePosition}</Box>);
    }

    if (rank) {
      items.push(<Box key="rank">Rank: {rank}</Box>);
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: isSmall ? "column" : "row",
          gap: 1,
          marginLeft: isSmall ? 0 : 2,
        }}
      >
        {items.map((item, index) => (
          <React.Fragment key={index}>
            {item}
            {!isSmall && index < items.length - 1 && <Box>|</Box>}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const hasAssignmentData = Object.values(latestAssignment).some(Boolean);

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <Assignment sx={{ mr: 1 }} />
              <span>Assignment History</span>

              {isSmall
                ? hasAssignmentData && (
                    <InfoIcon
                      sx={{ marginLeft: 0.5, marginRight: 0.5 }}
                      onClick={() => openDataDialog()}
                    />
                  )
                : renderAssignmentData()}
            </Box>
            <Box sx={{ mr: 2 }}>
              {!readOnly && canAdd && (
                <PATAddButton
                  tooltipText="Add Assignment History"
                  onClick={handleOpenAddAssignmentHistory}
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {personnelAssignmentHistory?.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={personnelAssignmentHistory}
              renderRow={(ah, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {formatDateToMonthDateYear(ah.effectiveDate)}
                  </TableCell>
                  <TableCell>
                    {ah.departmentInfo?.description || ah.department}
                  </TableCell>
                  <TableCell>
                    {formatDateToMonthDateYear(ah.departmentDate)}
                  </TableCell>
                  <TableCell>
                    {ah.titlePositionInfo?.description || ah.titlePosition}
                  </TableCell>
                  <TableCell>{ah.rankInfo?.description || ah.rank}</TableCell>
                  <TableCell>
                    {ah.sectionInfo?.description || ah.section}
                  </TableCell>
                  <TableCell>
                    {formatDateToMonthDateYear(ah.sectionDate)}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {!readOnly && canEdit && (
                        <PATEditButton
                          tooltipText="Edit Assignment History"
                          onClick={(e) => handleOpenAssignmentHistory(e, ah)}
                          size="20px"
                        />
                      )}
                      {!readOnly && canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Assignment History"
                          onClick={(e) => handleOpenDeleteDialog(e, ah.id)}
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Assignment History Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <AssignmentHistoryDialog
        open={openAssignmentHistoryDialog}
        onClose={() => setOpenAssignmentHistoryDialog(false)}
        onSave={handleSaveAssignmentHistory}
        initialData={editingAssignmentHistory}
        departmentOptions={departmentOptions}
        titlePositionOptions={titlePositionOptions}
        rankOptions={rankOptions}
        sectionOptions={sectionOptions}
      />
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setAssignmentHistoryToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Personnel Assignment History"
        message="Are you sure you want to delete this personnel assignment history?"
      />
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Current Assignment</DialogTitle>
        <DialogContent>{renderAssignmentData()}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            style={{ background: "#f1f1f1", color: "black" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssignmentHistoryTable;
